import { useMutation } from '@apollo/client/react';
import { NEXT_PUBLIC_API_URL } from '@env';
import { logger } from '@modules/logger';
import { useEffect, useMemo } from 'react';

import { CreateOneTimeLoginTokenDocument } from './createOneTimeLoginToken.graphql';

interface IOpenAppUrlOptions {
  includeOpaqueToken?: boolean;
  /**
   * Use case for opening the app
   * monorepo/api/src/route/redirect/use-cases.ts
   */
  useCase?: 'claim' | 'signup';
}

/** Created an "Open App" deeplink */
export function useOpaqueToken({ includeOpaqueToken }: IOpenAppUrlOptions): string {
  const [createOneTimeLoginTokenMutation, result] = useMutation(CreateOneTimeLoginTokenDocument);
  const enabled = includeOpaqueToken;

  useEffect(() => {
    if (enabled) {
      try {
        createOneTimeLoginTokenMutation();
      } catch (error) {
        logger.error('Error creating one time login token', { error });
      }
    }
  }, [enabled, createOneTimeLoginTokenMutation]);

  return useMemo(() => result.data?.createOneTimeLoginToken ?? '', [result]); // memoise the result
}

/** Get a link to the play or app store */
export function useOpenAppUrl(options: IOpenAppUrlOptions): {
  appStore: string;
  playStore: string;
} {
  const opaqueToken = useOpaqueToken(options);
  const appStoreUrl = new URL('/redirect/blinq-app', NEXT_PUBLIC_API_URL);
  const playStoreUrl = new URL('/redirect/blinq-app', NEXT_PUBLIC_API_URL);
  // set the platform value
  appStoreUrl.searchParams.set('p', 'ios');
  playStoreUrl.searchParams.set('p', 'android');

  const { useCase } = options;
  if (useCase) {
    appStoreUrl.searchParams.set('use', useCase);
    playStoreUrl.searchParams.set('use', useCase);
  }

  if (opaqueToken) {
    appStoreUrl.searchParams.set('opaque_token', opaqueToken);
    playStoreUrl.searchParams.set('opaque_token', opaqueToken);
  }
  return { appStore: appStoreUrl.toString(), playStore: playStoreUrl.toString() };
}
